<template lang="pug">
v-dialog(v-model="dialog" width="500" persistent)
  v-card
    v-card-title {{ title }}
    v-card-text
      v-form(ref="form" @submit.prevent="onSubmit")
        v-text-field(
          label="Nombre del cliente*"
          v-model="form.name"
          outlined
          @input="responseErrors.name = null"
          :rules="v.name")
        v-combobox(
          label="Clasificación*"
          v-model="form.rubro"
          outlined
          :items="rubros"
          item-text="name"
          :rules="v.rubro"
          return-object
          hide-selected
          hint="Elige uno o escríbelo para crear uno nuevo"
          persistent-hint
          @change="onRubroChange")
        v-combobox(
          label="Sub-clasificación (opcional)"
          v-model="form.subrubro"
          outlined
          :items="subrubros"
          item-text="name"
          return-object
          hide-selected
          hint="Elige uno o escríbelo para crear uno nuevo"
          persistent-hint
          clearable)
        v-text-field(
          label="Dirección (opcional)"
          v-model="form.address"
          outlined)
        v-autocomplete(
          label="Comuna*"
          v-model="form.commune"
          outlined
          :items="communes"
          :rules="v.commune"
          item-value="id"
          item-text="name")


        .text-right
          v-btn(color="primary" outlined @click="closeDialog" :disabled="loading").mr-2 Cancelar
          v-btn(color="primary" type="submit" :loading="loading") Guardar
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import FormMixin from '@/components/mixins/FormMixin'
import validations from '@/shared/formValidations'
import { WIZARD_STEPS_EVENTS } from '@/constants'
import { globalEmitter } from '../../utils/eventBus';

export default {
  mixins: [FormMixin],
  data () {
    return {
      instanceId: null,
      form: {
        name: '',
        classification: null,
        rubro: null,
        subrubro: null,
        address: '',
        commune: null,
      },
      communes: [],
      classifications: [],
      dialog: false,
      responseErrors: {}
    }
  },
  methods: {
    ...mapActions('customer', ['createCustomer', 'editCustomer', 'getCustomer', 'listCommunes']),
    ...mapActions('business', ['listCustomerClassifications', 'createCustomerClassification']),
    onRubroChange () {
      this.form.subrubro = null
    },
    // Devuelve el id de la clasificación (rubro o sub-rubro), creándola si es nueva.
    async ensureClassification (val, parentId) {
      if (!val) return null
      if (typeof val === 'object' && val.id) return val.id
      const name = (typeof val === 'string' ? val : (val.name || '')).trim()
      if (!name) return null
      const existing = this.classifications.find(c =>
        c.name.toLowerCase() === name.toLowerCase() && (c.parent || null) === (parentId || null))
      if (existing) return existing.id
      const { data } = await this.createCustomerClassification({ data: { name, parent: parentId } })
      this.classifications.push(data)
      return data.id
    },
    async resolveClassification () {
      const rubroId = await this.ensureClassification(this.form.rubro, null)
      if (!rubroId) return null
      if (this.form.subrubro) {
        const subId = await this.ensureClassification(this.form.subrubro, rubroId)
        return subId || rubroId
      }
      return rubroId
    },
    async onSubmit () {
      if (!this.$refs.form.validate()) return
      this.loading = true
      try {
        this.form.classification = await this.resolveClassification()
      } catch (e) {
        this.loading = false
        this.$toast.error('No se pudo crear el rubro/sub-rubro.')
        return
      }
      const action = this.getAction()
      const response = await action({ ...this.getData() })
      this.loading = false
      if (response.status >= 200 && response.status < 300) {
        this.onSuccess(response)
        this.$emit('save', response.data)
      } else {
        this.onError(response)
      }
    },
    async openDialog (instanceId) {
      this.instanceId = instanceId
      await this.fetchForm()
      this.dialog = true
    },
    closeDialog () {
      this.dialog = false
    },
    onSuccess () {
      this.dialog = false
      this.$toast.success(this.instanceId ? 'Cliente actualizado' : 'Cliente creado con éxito')
      globalEmitter(WIZARD_STEPS_EVENTS.CLIENT_CREATED)
      this.$emit('save')
    },
    onError (response) {
      const data = (response && (response.data || (response.error && response.error.response && response.error.response.data))) || {}
      this.responseErrors = data
      this.$toast.error('No se pudo guardar el cliente. Revisa los datos.')
    },
    getAction () {
      return this.instanceId ? this.editCustomer : this.createCustomer
    },
    async fetchForm () {
      if (!this.communes.length) {
        const { data: communes } = await this.listCommunes()
        this.communes = communes
      }
      if (!this.classifications.length) {
        const { data: classifications } = await this.listCustomerClassifications()
        this.classifications = classifications
      }
      if (this.instanceId) {
        const { data } = await this.getCustomer(this.instanceId)
        const cls = this.classifications.find(c => c.id === data.classification)
        let rubro = null, subrubro = null
        if (cls) {
          if (cls.parent) { rubro = this.classifications.find(c => c.id === cls.parent) || null; subrubro = cls }
          else { rubro = cls }
        }
        this.form = {
          name: data.name,
          classification: data.classification,
          rubro,
          subrubro,
          address: data.address,
          commune: data.commune,
        }
      }
    },
    getData () {
      const data = { data: this.form }
      if (this.instanceId) {
        data.id = this.instanceId
      }
      return data
    },
    resetForm () {
      this.form = {
        name: '',
        classification: null,
        rubro: null,
        subrubro: null,
        address: '',
        commune: '',
      }
      this.$refs.form.resetValidation()
    },
    onClose () {
      this.instanceId = null
      this.resetForm()
    }
  },
  computed: {
    ...mapGetters('app_config', ['currentBusiness']),
    title () {
      return this.instanceId ? 'Editar cliente' : 'Agregar cliente'
    },
    rubros () {
      return this.classifications.filter(c => !c.parent)
    },
    subrubros () {
      const r = this.form.rubro
      const rid = r && typeof r === 'object' ? r.id : null
      if (!rid) return []
      return this.classifications.filter(c => c.parent === rid)
    },
    v () {
      return {
        name: [validations.isRequired, ...(this.responseErrors.name ? [this.responseErrors.name] : [])],
        rubro: [validations.isRequired],
        commune: [validations.isRequired],
      }
    }
  },
  watch: {
    dialog () {
      if (!this.dialog) {
        this.onClose()
      }
    },
    'form.name' (newValue, oldValue) {
      if (oldValue !== newValue) {
        this.form.name = newValue.toUpperCase()
      }
    }
  }
}
</script>